import Box from "@mui/material/Box";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Roles } from "assets/SideBarIcons/roles.svg";
import Roletabs from "./Roletabs";
import { useGetRolePrivilegeService } from "subModule/src/services/role/useRole";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Checkbox } from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import Loader from "layout/Loader";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { RoleProfileService } from "subModule/src/core/services";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

export default function RolePrivileges() {
  const params = useParams();
  const roleId = params?.id;
  const navigate = useNavigate();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];

  // True only during the initial page data load
  const [loading, setLoading] = useState<boolean>(true);
  // Tracks individually-toggled privilege IDs currently awaiting API response
  const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set());
  // Tracks group-level operations: keys are `parent-{id}` or `grandParent-{id}`
  const [loadingGroups, setLoadingGroups] = useState<Set<string>>(new Set());
  // Local copy of privilege list — enables optimistic updates without full refetch
  const [localPrivileges, setLocalPrivileges] = useState<any[]>([]);
  const [roleTitle, setRoleTitle] = useState("");

  const [accessLimit, setAccessLimit] = useState<number | null>(null);
  const [expandedGrandParents, setExpandedGrandParents] = useState<number[]>(
    [],
  );
  const [expandedParents, setExpandedParents] = useState<number[]>([]);

  const {
    data: tableData,
    isLoading,
    error,
    refetch,
  } = useGetRolePrivilegeService(
    { pageNumber: 1, size: 1000 },
    +(params as any).id,
  );
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  useEffect(() => {
    if (!roleId) return;
    let active = true;
    RoleProfileService.getRoleByID(+roleId)
      .then((res: any) => {
        if (!active) return;
        const name = String(
          res?.data?.title ||
            res?.data?.secondaryTitle ||
            res?.title ||
            "",
        ).trim();
        if (name) setRoleTitle(name);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [roleId]);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { labelKey: "general" },
      { labelKey: "Roles", href: "/roles" },
      {
        labelKey: "title",
        ...(roleTitle ? { label: roleTitle } : {}),
        href: roleId ? `/role/${roleId}` : undefined,
      },
      { labelKey: "privileges" },
    ],
    [roleId, roleTitle],
  );

  useEffect(() => {
    if (!isLoading && !error) refetch();
  }, []);

  useEffect(() => {
    if (tableData) {
      setLocalPrivileges(tableData?.pages[0]?.data?.content || []);
      setLoading(false);
      if (
        (tableData?.pages[0] as any).status &&
        !(tableData?.pages[0] as any).success
      ) {
        enqueueSnackbar(
          <AlertMessages statusCode={(tableData?.pages[0] as any)?.status} />,
          { variant: "error" },
        );
      }
    }
  }, [tableData]);

  // ── Loading state helpers ──────────────────────────────────────────────────

  const addLoadingId = useCallback((id: number) => {
    setLoadingIds((prev) => new Set([...prev, id]));
  }, []);

  const removeLoadingId = useCallback((id: number) => {
    setLoadingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const addLoadingGroup = useCallback((key: string) => {
    setLoadingGroups((prev) => new Set([...prev, key]));
  }, []);

  const removeLoadingGroup = useCallback((key: string) => {
    setLoadingGroups((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  // Is a specific privilege item currently loading?
  // Covers: individual toggle, parent-group op, or grand-parent-group op.
  const isItemLoading = (
    itemId: number,
    parentId: number | null,
    grandParentId: number,
  ): boolean =>
    loadingIds.has(itemId) ||
    (parentId !== null && loadingGroups.has(`parent-${parentId}`)) ||
    loadingGroups.has(`grandParent-${grandParentId}`);

  // Is a parentItem's own row (where parentId is null in data) loading?
  const isParentRowLoading = (
    parentId: number,
    grandParentId: number,
  ): boolean =>
    loadingIds.has(parentId) ||
    loadingGroups.has(`parent-${parentId}`) ||
    loadingGroups.has(`grandParent-${grandParentId}`);

  // Is the parent accordion header (select-all checkbox) loading?
  const isParentHeaderLoading = (
    parentId: number,
    grandParentId: number,
  ): boolean =>
    loadingGroups.has(`parent-${parentId}`) ||
    loadingGroups.has(`grandParent-${grandParentId}`);

  // Is the grand-parent accordion header loading?
  const isGrandParentHeaderLoading = (grandParentId: number): boolean =>
    loadingGroups.has(`grandParent-${grandParentId}`);

  // ── Derived data (always from localPrivileges for optimistic accuracy) ─────

  const GRAND_PARENT_GROUPS: Record<number, string> = {
    1: "adminSystemSetup",
    2: "inventoryStockManagemnet",
    3: "vehicleFleetManagement",
    4: "serviceOperations",
    5: "financeSalesOperations",
    6: "crmCustomersVendors",
    7: "reportsAnalytics",
    8: "miscStandalone",
  };

  const groupedParents = Object.entries(GRAND_PARENT_GROUPS).map(
    ([grandParentId, heading]) => ({
      heading,
      items: localPrivileges.filter(
        (item: any) =>
          item.parentId === null &&
          item.grandParentId === Number(grandParentId),
      ),
    }),
  );

  const isGrandParentChecked = (grandParentId: number) => {
    const items = localPrivileges.filter(
      (item: any) => item.grandParentId === grandParentId,
    );
    return items.length > 0 && items.every((item: any) => item.linked);
  };

  // ── Action handlers ───────────────────────────────────────────────────────

  const assignUnassignSingleEntity = async (
    isChecked: boolean,
    row: any,
    _scroll: number,
    limit?: number | null,
    grandParentId?: number,
    parentId?: number,
  ) => {
    if (grandParentId)
      setExpandedGrandParents((prev) =>
        prev.includes(grandParentId) ? prev : [...prev, grandParentId],
      );
    if (parentId)
      setExpandedParents((prev) =>
        prev.includes(parentId) ? prev : [...prev, parentId],
      );

    // Optimistic update — flip linked state immediately
    setLocalPrivileges((prev) =>
      prev.map((item) =>
        item.id === row.id ? { ...item, linked: isChecked } : item,
      ),
    );
    addLoadingId(row.id);

    const payload = {
      roleId: +(params as any).id,
      privilegeRequests: [{ privilegeId: row.id, accessLimit: Number(limit) }],
    };

    try {
      const response = isChecked
        ? await RoleProfileService.assignAllPrivileges(payload)
        : await RoleProfileService.unassignAllPrivileges(payload);

      if (response.success) {
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("Privileges") +
              " " +
              (isChecked ? t("assigned") : t("unassigned")) +
              " " +
              t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        // Revert optimistic update on failure
        setLocalPrivileges((prev) =>
          prev.map((item) =>
            item.id === row.id ? { ...item, linked: !isChecked } : item,
          ),
        );
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    } catch (err: any) {
      setLocalPrivileges((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, linked: !isChecked } : item,
        ),
      );
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) console.error(err.stack);
    } finally {
      removeLoadingId(row.id);
    }
  };

  const assignUnassignAllEntities = async (
    isChecked: boolean,
    rowIds: number[],
    _scroll: number,
    limit?: number | null,
  ) => {
    const parentId = rowIds[0];
    const groupKey = `parent-${parentId}`;

    // Optimistic update for all items in this group
    setLocalPrivileges((prev) =>
      prev.map((item) =>
        rowIds.includes(item.id) ? { ...item, linked: isChecked } : item,
      ),
    );
    addLoadingGroup(groupKey);

    const payload = {
      roleId: +(params as any).id,
      privilegeRequests: rowIds.map((id) => ({
        privilegeId: id,
        accessLimit: limit ?? null,
      })),
    };

    try {
      const response = isChecked
        ? await RoleProfileService.assignAllPrivileges(payload)
        : await RoleProfileService.unassignAllPrivileges(payload);

      if (response.success) {
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("Privileges") +
              " " +
              (isChecked ? t("assigned") : t("unassigned")) +
              " " +
              t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        setLocalPrivileges((prev) =>
          prev.map((item) =>
            rowIds.includes(item.id) ? { ...item, linked: !isChecked } : item,
          ),
        );
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    } catch (err: any) {
      setLocalPrivileges((prev) =>
        prev.map((item) =>
          rowIds.includes(item.id) ? { ...item, linked: !isChecked } : item,
        ),
      );
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) console.error(err.stack);
    } finally {
      removeLoadingGroup(groupKey);
    }
  };

  const assignUnassignGroupEntities = async (
    isChecked: boolean,
    grandParentId: number,
  ) => {
    const groupKey = `grandParent-${grandParentId}`;
    const affectedIds = localPrivileges
      .filter((item: any) => item.grandParentId === grandParentId)
      .map((item: any) => item.id);

    // Optimistic update for the entire grand-parent section
    setLocalPrivileges((prev) =>
      prev.map((item) =>
        item.grandParentId === grandParentId
          ? { ...item, linked: isChecked }
          : item,
      ),
    );
    addLoadingGroup(groupKey);

    try {
      const response = isChecked
        ? await RoleProfileService.assignGroupPrivileges({
            roleId: +(params as any).id,
            grandParentId,
          })
        : await RoleProfileService.unassignGroupPrivileges({
            roleId: +(params as any).id,
            grandParentId,
          });

      if (response.success) {
        enqueueSnackbar(
          <AlertMessages
            statusCode={response.status}
            text={
              t("group") +
              " " +
              t("privilege") +
              " " +
              (isChecked ? t("assigned") : t("unassigned")) +
              " " +
              t("successfully")
            }
          />,
          { variant: "success" },
        );
      } else {
        setLocalPrivileges((prev) =>
          prev.map((item) =>
            affectedIds.includes(item.id)
              ? { ...item, linked: !isChecked }
              : item,
          ),
        );
        enqueueSnackbar(<AlertMessages statusCode={response.status} />, {
          variant: "error",
        });
      }
    } catch (err: any) {
      setLocalPrivileges((prev) =>
        prev.map((item) =>
          affectedIds.includes(item.id)
            ? { ...item, linked: !isChecked }
            : item,
        ),
      );
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) console.error(err.stack);
    } finally {
      removeLoadingGroup(groupKey);
    }
  };

  const handleParentCheckboxChange = (
    parent: any,
    isChecked: boolean,
    grandParentId: number,
  ) => {
    setExpandedGrandParents((prev) =>
      prev.includes(grandParentId) ? prev : [...prev, grandParentId],
    );
    setExpandedParents((prev) =>
      prev.includes(parent.id) ? prev : [...prev, parent.id],
    );
    setAccessLimit(null);
    const groupIds = [
      parent,
      ...localPrivileges.filter((item: any) => item.parentId === parent.id),
    ].map((item: any) => item.id);
    assignUnassignAllEntities(isChecked, groupIds, window.scrollY, accessLimit);
  };

  const handleGrandParentCheckboxChange = (
    grandParentId: number,
    isChecked: boolean,
  ) => {
    setExpandedGrandParents((prev) =>
      prev.includes(grandParentId) ? prev : [...prev, grandParentId],
    );
    assignUnassignGroupEntities(isChecked, grandParentId);
  };

  // ── Shared styles ─────────────────────────────────────────────────────────

  const spinnerSx = { color: themeColors?.primary, margin: "9px" };
  const checkboxBaseSx = {
    "& svg": { fill: themeColors?.primary },
    opacity: 1,
    "&.Mui-disabled": { opacity: 0.3 },
  };
  const checkboxCompactSx = {
    ...checkboxBaseSx,
    paddingTop: 0,
    paddingBottom: 0,
  };

  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/roles")}
        heading={t("editRoles")}
        icon={<Roles height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
        breadcrumbItems={breadcrumbItems}
      />
      <Box>
        <div className="flex">
          {params?.id ? <Roletabs params={params} selectedIndex={1} /> : null}
          {loading && (
            <Box sx={{ "& .MuiBackdrop-root": { zIndex: 999 } }}>
              <Loader />
            </Box>
          )}
          <Box
            className="w-[100%] mt-3"
            sx={{
              "& .MuiPaper-root": {
                "&:before": { display: "none" },
                boxShadow: "none",
                borderRadius: "8px",
                margin: "4px 0",
                "&.MuiAccordion-root.Mui-expanded": {
                  margin: "4px 0 !important",
                },
              },
            }}
          >
            {groupedParents.map((group: any) => {
              const grandParentId = Number(
                Object.keys(GRAND_PARENT_GROUPS).find(
                  (key) => GRAND_PARENT_GROUPS[Number(key)] === group.heading,
                ),
              );
              const gpHeaderLoading = isGrandParentHeaderLoading(grandParentId);

              return (
                group.items.length > 0 && (
                  <Accordion
                    key={group.heading}
                    sx={{ background: "transparent" }}
                    expanded={expandedGrandParents.includes(grandParentId)}
                    onChange={(_, isExpanded) =>
                      setExpandedGrandParents((prev) =>
                        isExpanded
                          ? [...prev, grandParentId]
                          : prev.filter((id) => id !== grandParentId),
                      )
                    }
                  >
                    <AccordionSummary
                      sx={{
                        flexDirection: "row-reverse",
                        padding: "0",
                        borderBottom: "1px solid" + themeColors?.primary,
                        color: themeColors?.primary,
                        minHeight: "10px !important",
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          margin: "0",
                        },
                        "& .Mui-expanded": { margin: "0 !important" },
                      }}
                      expandIcon={
                        <span>
                          <ArrowRightIcon
                            sx={{
                              fill: themeColors?.primary,
                              marginLeft: "-8px",
                            }}
                          />
                        </span>
                      }
                    >
                      <Typography className="font-Saira font-[600] text-[15px] text-left flex items-center">
                        {gpHeaderLoading ? (
                          <CircularProgress size={20} sx={spinnerSx} />
                        ) : (
                          <Checkbox
                            checked={isGrandParentChecked(grandParentId)}
                            onChange={(e) =>
                              handleGrandParentCheckboxChange(
                                grandParentId,
                                e.target.checked,
                              )
                            }
                            sx={checkboxBaseSx}
                          />
                        )}
                        <span>{t(group.heading)}</span>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: "4px" }}>
                      {group.items.map(
                        (parentItem: any, parentIndex: number) => {
                          const pHeaderLoading = isParentHeaderLoading(
                            parentItem.id,
                            grandParentId,
                          );
                          return (
                            <Accordion
                              key={parentItem.id}
                              defaultExpanded={parentIndex === 0}
                              expanded={expandedParents.includes(parentItem.id)}
                              onChange={(_, isExpanded) =>
                                setExpandedParents((prev) =>
                                  isExpanded
                                    ? [...prev, parentItem.id]
                                    : prev.filter((id) => id !== parentItem.id),
                                )
                              }
                            >
                              <AccordionSummary
                                expandIcon={
                                  <span>
                                    <KeyboardArrowDownIcon />
                                  </span>
                                }
                                sx={{
                                  padding: "0 5px",
                                  minHeight: "20px !important",
                                  "& .MuiAccordionSummary-content": {
                                    alignItems: "center",
                                    margin: "3px 0",
                                  },
                                  "& .Mui-expanded": { margin: "0 !important" },
                                }}
                              >
                                {pHeaderLoading ? (
                                  <CircularProgress size={20} sx={spinnerSx} />
                                ) : (
                                  <Checkbox
                                    checked={
                                      parentItem.linked &&
                                      localPrivileges
                                        .filter(
                                          (item: any) =>
                                            item.parentId === parentItem.id,
                                        )
                                        .every((child: any) => child.linked)
                                    }
                                    onChange={(event) =>
                                      handleParentCheckboxChange(
                                        parentItem,
                                        event.target.checked,
                                        grandParentId,
                                      )
                                    }
                                    disabled={
                                      checkPrivileges(
                                        "ROLE_ROLE_PRIVILEGE_ASSIGN",
                                      ) && !parentItem.linked
                                        ? false
                                        : checkPrivileges(
                                              "ROLE_ROLE_PRIVILEGE_UNASSIGN",
                                            ) && parentItem.linked
                                          ? false
                                          : true
                                    }
                                    sx={checkboxBaseSx}
                                  />
                                )}
                                <Typography className="font-Saira font-[600] text-[13px] text-txt-color">
                                  {parentItem.erole !==
                                    "ROLE_JOBCARD_DISCOUNT_UPDATE" &&
                                  parentItem.erole !==
                                    "ROLE_WORKORDER_DISCOUNT_UPDATE" &&
                                  parentItem.erole !==
                                    "ROLE_CART_DISCOUNT_UPDATE" &&
                                  parentItem.erole !==
                                    "ROLE_ESTIMATION_DISCOUNT_UPDATE"
                                    ? t(
                                        parentItem?.erole
                                          ?.split?.("_")
                                          ?.slice?.(1, -1)
                                          ?.join?.(" ") || "",
                                      )
                                    : t(parentItem.erole)}
                                </Typography>
                              </AccordionSummary>
                              {parentItem.erole ===
                                "ROLE_JOBCARD_DISCOUNT_UPDATE" ||
                              parentItem.erole ===
                                "ROLE_WORKORDER_DISCOUNT_UPDATE" ||
                              parentItem.erole ===
                                "ROLE_CART_DISCOUNT_UPDATE" ||
                              parentItem.erole ===
                                "ROLE_ESTIMATION_DISCOUNT_UPDATE" ? (
                                <div className="flex w-100 items-center font-Saira">
                                  <label className="pl-[16px] pe-[10px] font-bold leading-[21px] text-[13px]">
                                    {t("limit")} :{" "}
                                  </label>
                                  <input
                                    type="number"
                                    id="formfield"
                                    min={0}
                                    max={100}
                                    className="w-[200px]"
                                    defaultValue={parentItem.accessLimit}
                                    onChange={(e) =>
                                      setAccessLimit(
                                        (e as any).target.value < 0
                                          ? 0
                                          : (e as any).target.value > 100
                                            ? 100
                                            : (e as any).target.value,
                                      )
                                    }
                                  />
                                  <span className="ps-[5px] leading-[21px] text-[13px]">
                                    %
                                  </span>
                                </div>
                              ) : (
                                ""
                              )}
                              <AccordionDetails className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[10px]">
                                {parentItem.erole !==
                                  "ROLE_JOBCARD_DISCOUNT_UPDATE" &&
                                  parentItem.erole !==
                                    "ROLE_WORKORDER_DISCOUNT_UPDATE" &&
                                  parentItem.erole !==
                                    "ROLE_CART_DISCOUNT_UPDATE" &&
                                  parentItem.erole !==
                                    "ROLE_ESTIMATION_DISCOUNT_UPDATE" && (
                                    <Box
                                      key={parentItem.id}
                                      className="flex text-left items-center"
                                    >
                                      {isParentRowLoading(
                                        parentItem.id,
                                        grandParentId,
                                      ) ? (
                                        <CircularProgress
                                          size={20}
                                          sx={spinnerSx}
                                        />
                                      ) : (
                                        <Checkbox
                                          checked={parentItem.linked}
                                          disabled={
                                            checkPrivileges(
                                              "ROLE_ROLE_PRIVILEGE_ASSIGN",
                                            ) && !parentItem.linked
                                              ? false
                                              : checkPrivileges(
                                                    "ROLE_ROLE_PRIVILEGE_UNASSIGN",
                                                  ) && parentItem.linked
                                                ? false
                                                : true
                                          }
                                          onClick={(event) => {
                                            assignUnassignSingleEntity(
                                              (event.target as HTMLInputElement)
                                                .checked,
                                              parentItem,
                                              window.scrollY,
                                              null,
                                              grandParentId,
                                              parentItem.id,
                                            );
                                          }}
                                          sx={checkboxCompactSx}
                                        />
                                      )}
                                      <Typography className="font-Saira font-[500] text-[13px] text-txt-color">
                                        {t(parentItem.erole)}
                                      </Typography>
                                    </Box>
                                  )}
                                {localPrivileges
                                  .filter(
                                    (item: any) =>
                                      item.parentId === parentItem.id,
                                  )
                                  .map((childItem: any) => (
                                    <Box
                                      key={childItem.id}
                                      className="flex text-left items-center"
                                    >
                                      {isItemLoading(
                                        childItem.id,
                                        childItem.parentId,
                                        grandParentId,
                                      ) ? (
                                        <CircularProgress
                                          size={20}
                                          sx={spinnerSx}
                                        />
                                      ) : (
                                        <Checkbox
                                          checked={childItem.linked}
                                          onClick={(event) => {
                                            assignUnassignSingleEntity(
                                              (event.target as HTMLInputElement)
                                                .checked,
                                              childItem,
                                              window.scrollY,
                                              null,
                                              grandParentId,
                                              parentItem.id,
                                            );
                                          }}
                                          disabled={
                                            checkPrivileges(
                                              "ROLE_ROLE_PRIVILEGE_ASSIGN",
                                            ) && !childItem.linked
                                              ? false
                                              : checkPrivileges(
                                                    "ROLE_ROLE_PRIVILEGE_UNASSIGN",
                                                  ) && childItem.linked
                                                ? false
                                                : true
                                          }
                                          sx={checkboxCompactSx}
                                        />
                                      )}
                                      <Typography className="font-Saira font-[500] text-[13px] text-txt-color">
                                        {t(childItem.erole)}
                                        {/* {childItem.accessLimit ? ' - Limit : ' + childItem.accessLimit : ''} */}
                                      </Typography>
                                    </Box>
                                  ))}
                              </AccordionDetails>
                            </Accordion>
                          );
                        },
                      )}
                    </AccordionDetails>
                  </Accordion>
                )
              );
            })}
          </Box>
        </div>
      </Box>
    </Box>
  );
}
